import type { KeepAwakeService } from "../types"

let held = false
let sentinel: WakeLockSentinel | null = null
// The request in flight, so a quick release-then-hold doesn't start a second
// one and orphan the first sentinel.
let pending: Promise<void> | null = null

/**
 * Request a lock. Resolves to true when the lock it got was released again
 * because the hold had ended by the time it arrived.
 */
async function request(): Promise<boolean> {
  try {
    const next = await navigator.wakeLock.request("screen")
    if (!held) {
      await next.release()
      return true
    }
    sentinel = next
    next.addEventListener(
      "release",
      () => {
        if (sentinel === next) sentinel = null
      },
      { once: true },
    )
  } catch (error) {
    console.warn("[KeepAwake] Screen wake lock unavailable:", error)
  }
  return false
}

function acquire(): Promise<void> {
  if (!held || sentinel || !("wakeLock" in navigator)) return Promise.resolve()
  if (document.visibilityState !== "visible") return Promise.resolve()
  if (!pending) {
    pending = request().then((discarded) => {
      pending = null
      // A hold that arrived while that lock was being let go joined this
      // request, which ended with no lock: ask again for it.
      if (discarded && held) return acquire()
    })
  }
  return pending
}

// The browser drops a screen wake lock whenever the page is hidden, so take
// it again on the way back while it's still wanted.
const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") void acquire()
}

export const keepAwake: KeepAwakeService = {
  async hold() {
    if (!held) {
      held = true
      document.addEventListener("visibilitychange", handleVisibilityChange)
    }
    await acquire()
  },
  async release() {
    held = false
    document.removeEventListener("visibilitychange", handleVisibilityChange)
    const current = sentinel
    sentinel = null
    await current?.release()
    // A request still in flight releases its own lock once it sees `held`
    // is false.
  },
}
